export const GENDER_OPTION_VALUES = [
  'Male at birth',
  'Female at birth',
  'Prefer not to disclose',
  'Other',
] as const;

export type GenderOptionValue = (typeof GENDER_OPTION_VALUES)[number];

/** Canonical value for the "Other" option — use in refine checks and UI comparisons. */
export const GENDER_OTHER_VALUE: GenderOptionValue = 'Other';

/** When gender is missing from stored/payload data, API expects this default. */
export const GENDER_DEFAULT_FALLBACK: GenderOptionValue = 'Prefer not to disclose';

/** Max length for free-text gender when `GENDER_OTHER_VALUE` is selected (matches backend). */
export const GENDER_OTHER_TEXT_MAX_LENGTH = 100;

/** Form `name` / object keys / HTML `id` roots for gender fields */
export const GENDER_FIELD_NAMES = {
  GENDER: 'gender',
  OTHER_GENDER: 'otherGender',
} as const;

/** User-facing copy and Zod messages for gender fields */
export const GENDER_MESSAGES = {
  REQUIRED: 'Gender is required',
  SPECIFY_WHEN_OTHER: 'Please specify gender',
  SPECIFY_WHEN_OTHER_ZOD: 'Please specify gender when selecting Other',
  SELECT_PLACEHOLDER: 'Select gender',
  OTHER_TEXT_PLACEHOLDER: 'Enter gender',
  OTHER_SPECIFY_LABEL: 'Please specify',
} as const;

/** Stable DOM ids for error announcements (pair with `aria-describedby`) */
export const GENDER_DOM_IDS = {
  GENDER_ERROR: 'gender-error',
  OTHER_GENDER_ERROR: 'otherGender-error',
} as const;

export const GENDER_OPTIONS: ReadonlyArray<{ value: GenderOptionValue; label: string }> =
  GENDER_OPTION_VALUES.map((value) => ({ value, label: value }));

export function isOtherGender(gender: string | null | undefined): boolean {
  return (gender ?? '') === GENDER_OTHER_VALUE;
}

export function toGenderOption(value: string | null | undefined): GenderOptionValue | '' {
  if (!value) {
    return '';
  }
  return GENDER_OPTION_VALUES.includes(value as GenderOptionValue) ? (value as GenderOptionValue) : '';
}
